import { useCallback, useEffect } from 'react';

import { getRouteApi, useNavigate } from '@tanstack/react-router';
import { Button } from 'cpool-components/input/Button';
import { ButtonVariant } from 'cpool-components/input/Button/types';
import type { SchemaLoanRequestResolver_Get } from 'cpool-tables/backend-resolvers';
import { useTranslation } from 'react-i18next';
import { LuUserPlus } from 'react-icons/lu';
import { toast } from 'react-toastify';

import { CreateApplicantModal } from './modals/CreateApplicantModal';
import { LEAD_DETAILS_OVERLAYS } from '~/features/leadDetails/constants/overlays';
import { CREATE_APPLICANT_TAB_PATHS } from '~/features/leadDetails/constants/tabPaths';
import { useCustomerDataTabsContext } from '~/features/leadDetails/context/CustomerDataTabsContext';
import { useLeadDetailsFormContext } from '~/features/leadDetails/context/LeadDetailsFormContext.tsx';
import { useAclPermissions } from '~/features/leadDetails/hooks/useAclPermissions';
import { updateLeadDetailsOverlaySearch } from '~/features/leadDetails/utils/normalizeLeadDetailsSearch';
import { getCreateApplicantTabFromPath } from '~/features/leadDetails/utils/tabPaths';

type Props = {
    lead: SchemaLoanRequestResolver_Get;
};

const leadDetailsRoute = getRouteApi('/_app/leads_/details/$leadId');

export const CreateApplicant: React.FC<Props> = ({ lead }) => {
    const { t } = useTranslation();
    const navigate = useNavigate({ from: '/leads/details/$leadId/customer-data' });
    const { createApplicant, createApplicantTab } = leadDetailsRoute.useSearch();

    const { hasNotifications } = useCustomerDataTabsContext();
    const { isDirty } = useLeadDetailsFormContext();

    const { permissions } = useAclPermissions({ loanRequestId: lead.id });
    const isCreateApplicantRoutineDisabled = !permissions.api.createApplicantRoutine;
    const getStartBlockMessage = () => {
        if (hasNotifications) return t('LEAD_DETAILS_GENERAL.CANNOT_SWITCH_APPLICANTS_WITH_NOTIFICATIONS');
        if (isDirty()) return t('LEAD_DETAILS_GENERAL.SAVE_BEFORE_SWITCHING_APPLICANTS');
        if (lead.antragsteller2) return t('LEAD_DETAILS_GENERAL.CANNOT_SWITCH_HIDDEN_APPLICANT');
        return null;
    };
    const hardBlockMessage = lead.antragsteller2 ? t('LEAD_DETAILS_GENERAL.CANNOT_SWITCH_HIDDEN_APPLICANT') : null;
    const isHardBlocked = hardBlockMessage !== null || isCreateApplicantRoutineDisabled;

    const close = useCallback(() => {
        navigate({
            replace: true,
            search: (previous) => ({
                ...previous,
                createApplicant: undefined,
                createApplicantTab: undefined,
            }),
        });
    }, [navigate]);

    useEffect(() => {
        if (!createApplicant || !isHardBlocked) return;
        if (hardBlockMessage) toast.info(hardBlockMessage);
        close();
    }, [close, createApplicant, hardBlockMessage, isHardBlocked]);

    const startAddingProcess = () => {
        const message = getStartBlockMessage();
        if (message) {
            toast.info(message);
            return;
        }

        navigate({
            search: (previous) => ({
                ...updateLeadDetailsOverlaySearch(previous, LEAD_DETAILS_OVERLAYS.CREATE_APPLICANT, true),
                createApplicantTab: CREATE_APPLICANT_TAB_PATHS.PERSONAL_DATA,
            }),
        });
    };

    return (
        <>
            <Button
                onClick={startAddingProcess}
                eventPreventDefault
                variant={ButtonVariant.SECONDARY}
                iconLeft={<LuUserPlus />}
                disabled={isCreateApplicantRoutineDisabled}
            >
                <span>{t('CREDIT_WORTHINESS.BUTTONS.ADD_APPLICANT')}</span>
            </Button>
            <CreateApplicantModal
                open={!!createApplicant && !isHardBlocked}
                onClose={close}
                leadId={lead.id}
                mainApplicantWohnsituation={lead.antragsteller1.wohnsituation}
                selectedTab={getCreateApplicantTabFromPath(createApplicantTab)}
                onSelectTab={(tab) =>
                    navigate({
                        search: (previous) => ({
                            ...previous,
                            createApplicant: true,
                            createApplicantTab: CREATE_APPLICANT_TAB_PATHS[tab],
                        }),
                    })
                }
            />
        </>
    );
};
